import { push } from 'connected-react-router';
import { paths } from '../../../routes/paths';
import CoreAPIService from '../../../utils/core-api';
import ObjectUtils from '../../../utils/objectUtils';
import { fetchAction } from '../../actions';
import {
    AreaByID,
    ComputedPrice,
    Extra,
    ExtraType,
    Price,
    RootArea,
    RootPricing,
    Service,
    SetByID,
    TariffArrayByID,
    TariffByID,
} from '../api-types';

export const name: string = 'pricing';

export const types = {
    GET_AREAS: name + '/GET_AREAS',
    GET_ROOT_AREAS: name + '/GET_ROOT_AREAS',
    GET_ROOT_TREE_AREAS: name + '/GET_ROOT_TREE_AREAS',
    GET_AREA: name + '/GET_AREA',
    CREATE_AREA: name + '/CREATE_AREA',
    EDIT_AREA: name + '/EDIT_AREA',
    DELETE_AREA: name + '/DELETE_AREA',

    GET_PRICING_SETS: name + '/GET_PRICING_SETS',
    GET_TREE_PRICING_SETS: name + '/GET_TREE_PRICING_SETS',
    GET_PRICING_SET: name + '/GET_PRICING_SET',
    EXPORT_PRICING_SET: name + '/EXPORT_PRICING_SET',
    IMPORT_PRICING_SET: name + '/IMPORT_PRICING_SET',
    CREATE_PRICING_SET: name + '/CREATE_PRICING_SET',
    DELETE_PRICING_SET: name + '/DELETE_PRICING_SET',

    GET_COMPANY_PRICING_SET: name + '/GET_COMPANY_PRICING_SET',
    CREATE_COMPANY_PRICING_SET: name + '/CREATE_COMPANY_PRICING_SET',
    DELETE_COMPANY_PRICING_SET: name + '/DELETE_COMPANY_PRICING_SET',

    GET_GROUP_PRICING_SET: name + '/GET_GROUP_PRICING_SET',
    CREATE_GROUP_PRICING_SET: name + '/CREATE_GROUP_PRICING_SET',
    DELETE_GROUP_PRICING_SET: name + '/DELETE_GROUP_PRICING_SET',

    GET_COMPANY_EXTRA: name + '/GET_COMPANY_EXTRA',
    CREATE_COMPANY_EXTRA: name + '/CREATE_COMPANY_EXTRA',
    DELETE_COMPANY_EXTRA: name + '/DELETE_COMPANY_EXTRA',

    GET_SERVICES: name + '/GET_SERVICES',

    GET_TARIFFS: name + '/GET_TARIFFS',
    GET_TARIFF: name + '/GET_TARIFF',
    CREATE_TARIFF: name + '/CREATE_TARIFF',
    EDIT_TARIFF: name + '/EDIT_TARIFF',
    DELETE_TARIFF: name + '/DELETE_TARIFF',
    SEARCH_TARIFFS: name + '/SEARCH_TARIFFS',
    COMPUTE_TARIFF: name + '/COMPUTE_TARIFF',
    RESET_LOCAL_TARIFF: name + '/RESET_LOCAL_TARIFF',

    GET_EXTRAS: name + '/GET_EXTRAS',
    GET_EXTRA: name + '/GET_EXTRA',
    CREATE_EXTRA: name + '/CREATE_EXTRA',
    EDIT_EXTRA: name + '/EDIT_EXTRA',
    DELETE_EXTRA: name + '/DELETE_EXTRA',
    GET_EXTRA_TYPES: name + '/GET_EXTRA_TYPES',
};

interface PricingState {
    root_areas: { data: RootArea };
    root_tree_areas: { data: RootArea };
    areas: AreaByID;
    pricing_sets: { data: Price[] };
    pricing_sets_tree: { data: RootPricing };
    sets: SetByID;
    services: { data: Service[] };
    tariffs: TariffByID;
    /**
     * The last answer from the price tester, null until one arrives -- as
     * RESET_LOCAL_TARIFF already sets it. An empty object here would read as an
     * answer to the screen, which now gates on having one rather than on the
     * price being truthy.
     */
    price: ComputedPrice | null;
    tariffs_list: TariffArrayByID;
    extras_list: Extra[];
    extras_types: ExtraType[];
}

const initialState: PricingState = {
    areas: {} as AreaByID,
    root_areas: { data: {} as RootArea },
    root_tree_areas: { data: {} as RootArea },
    pricing_sets: { data: [] },
    pricing_sets_tree: { data: {} as RootPricing },
    sets: {} as SetByID,
    services: { data: [] },
    tariffs: {} as TariffByID,
    price: null,
    tariffs_list: {} as TariffArrayByID,
    extras_list: [],
    extras_types: [],
};

const reducer = (state = initialState, action): PricingState => {
    const payload = action.payload;
    const parameters = action.parameters;
    const id = parameters ? parameters.id : 0;
    const parent_id = parameters ? parameters.parent_id : 0;
    const company = parameters ? parameters.company : 0;

    let newState;
    switch (action.type) {
        // AREAS
        case types.GET_AREAS:
            return {
                ...state,
                areas: payload.data.reduce(
                    (res, item) => {
                        return { ...res, [item.id]: { ...state.areas[item.id], ...item } };
                    },
                    { ...state.areas },
                ),
            };
        case types.GET_ROOT_AREAS:
            return {
                ...state,
                root_areas: payload,
            };
        case types.GET_ROOT_TREE_AREAS:
            return {
                ...state,
                root_tree_areas: payload,
            };
        case types.GET_AREA:
            return {
                ...state,
                areas: {
                    ...state.areas,
                    [id]: payload.data,
                },
            };
        case types.EDIT_AREA:
            return {
                ...state,
                areas: {
                    ...state.areas,
                    [id]: payload.data,
                },
            };
        case types.DELETE_AREA:
            newState = ObjectUtils.deepCopy(state);
            if (!parent_id) {
                newState.root_tree_areas.data.children = newState.root_tree_areas.data.children.filter(
                    (x) => x.id !== id,
                );
            } else {
                delete newState.areas[id];
            }
            return newState;

        // PRICING_SETS
        case types.GET_PRICING_SETS:
            return {
                ...state,
                pricing_sets: payload,
            };
        case types.GET_TREE_PRICING_SETS:
            return {
                ...state,
                pricing_sets_tree: payload,
            };
        case types.GET_PRICING_SET:
            return {
                ...state,
                sets: {
                    ...state.sets,
                    [id]: payload,
                },
            };
        case types.DELETE_PRICING_SET:
            newState = ObjectUtils.deepCopy(state);
            if (!parent_id) {
                newState.pricing_sets.data = newState.pricing_sets.data.filter((x) => x.id !== id);
            } else {
                delete newState.sets[id];
            }
            return newState;

        // COMPANY_PRICING_SET
        case types.DELETE_COMPANY_PRICING_SET:
            const company_to_pricing_set_id = parameters ? parameters.company_to_pricing_set_id : 0;
            if (!state.sets?.[id]?.data?.companies) {
                return state;
            }
            return {
                ...state,
                sets: {
                    ...state.sets,
                    [id]: {
                        ...state.sets[id],
                        data: {
                            ...state.sets[id].data,
                            companies: state.sets[id].data.companies.filter(
                                (x) => x.company_to_pricing_set_id !== company_to_pricing_set_id,
                            ),
                        },
                    },
                },
            };

        case types.CREATE_COMPANY_PRICING_SET:
            payload.data.infos = { name: company.name, id: company.id };
            const tmp = payload.data.id;
            payload.data.id = payload.data.company_id;
            payload.data.company_to_pricing_set_id = tmp;
            if (!state.sets[id].data.companies) {
                state.sets[id].data.companies = [];
            }
            state.sets[id].data.companies.push(payload.data);

            return {
                ...state,
                sets: {
                    ...state.sets,
                    [id]: {
                        ...state.sets[id],
                        data: {
                            ...state.sets[id].data,
                            companies: state.sets[id].data.companies,
                        },
                    },
                },
            };

        // SERVICES
        case types.GET_SERVICES:
            return {
                ...state,
                services: payload,
            };

        // TARIFFS
        case types.SEARCH_TARIFFS:
            return {
                ...state,
                tariffs_list: {
                    ...state.tariffs_list,
                    [id]: payload.data,
                },
            };
        case types.GET_TARIFF:
            return {
                ...state,
                tariffs: {
                    ...state.tariffs,
                    [id]: payload.data,
                },
            };

        case types.EDIT_TARIFF:
            return {
                ...state,
                tariffs: {
                    ...state.tariffs,
                    [id]: payload.data,
                },
            };

        case types.COMPUTE_TARIFF:
            return {
                ...state,
                price: payload.data,
            };

        case types.RESET_LOCAL_TARIFF:
            return {
                ...state,
                price: null,
            };

        case types.DELETE_TARIFF:
            const { idp, idt } = parameters;
            newState = ObjectUtils.deepCopy(state);
            newState.tariffs_list[idp] = newState.tariffs_list[idp].filter((x) => x.id !== idt);
            return newState;

        // EXTRAS
        case types.GET_EXTRAS:
            return {
                ...state,
                extras_list: payload.data,
            };
        case types.CREATE_EXTRA:
            newState = ObjectUtils.deepCopy(state);
            newState.extras_list[payload.data.id] = payload.data;
            return newState;

        case types.GET_EXTRA:
        case types.EDIT_EXTRA:
            newState = ObjectUtils.deepCopy(state);
            newState.extras_list[id] = payload.data;
            return newState;

        case types.DELETE_EXTRA:
            newState = ObjectUtils.deepCopy(state);
            newState.extras_list = newState.extras_list.filter((x) => x.id !== id);
            return newState;

        case types.GET_EXTRA_TYPES:
            return {
                ...state,
                extras_types: payload.data,
            };

        default:
            return state;
    }
};
export default reducer;

export const actions = {
    getAreas: () => fetchAction(CoreAPIService.get(`/core/pricing/areas`), types.GET_AREAS, {}),
    getRootAreas: () => fetchAction(CoreAPIService.get(`/core/pricing/areas/root`), types.GET_ROOT_AREAS, {}),
    getRootTreeAreas: () =>
        fetchAction(CoreAPIService.get(`/pricing/areas/root/childrenTree`), types.GET_ROOT_TREE_AREAS, {}),
    getArea: (id) => fetchAction(CoreAPIService.get(`/core/pricing/areas/${id}`), types.GET_AREA, { id }),
    createArea: (payload) =>
        fetchAction(
            CoreAPIService.post(`/core/pricing/areas`, payload),
            types.CREATE_AREA,
            {},
            {
                success: (dispatch) => {
                    const open = payload.parent_id ? `?id=${payload.parent_id}` : '';
                    dispatch(push(`${paths.pricingManageAreas}${open}`));
                },
            },
        ),
    editArea: (id, payload) =>
        fetchAction(
            CoreAPIService.put(`/core/pricing/areas/${id}`, payload),
            types.EDIT_AREA,
            { id },
            {
                success: (dispatch) => {
                    dispatch(push(`${paths.pricingManageAreas}?id=${id}`));
                },
            },
        ),
    deleteArea: (id, parent_id?) =>
        fetchAction(
            CoreAPIService.delete(`/core/pricing/areas/${id}`),
            types.DELETE_AREA,
            { id, parent_id },
            {
                success: (dispatch) => {
                    const open = parent_id ? `?id=${parent_id}` : '';
                    dispatch(push(`${paths.pricingManageAreas}${open}`));
                },
            },
        ),

    getPricingSets: () => fetchAction(CoreAPIService.get(`/core/pricing/pricingSets`), types.GET_PRICING_SETS, {}),
    getPricingSetsTree: () =>
        fetchAction(CoreAPIService.get(`/pricing/pricingSets/root/childrenTree`), types.GET_TREE_PRICING_SETS, {}),
    getPricingSet: (id) =>
        fetchAction(CoreAPIService.get(`/core/pricing/pricingSets/${id}`), types.GET_PRICING_SET, { id }),
    exportPricingSet: (id, callback) =>
        fetchAction(
            CoreAPIService.get(`/pricing/pricingSets/${id}/export`),
            types.EXPORT_PRICING_SET,
            { id },
            { success: callback },
        ),
    importPricingSet: (id, payload, callback) =>
        fetchAction(
            CoreAPIService.post(`/pricing/pricingSets/${id}/import`, payload),
            types.IMPORT_PRICING_SET,
            { id, payload },
            { success: callback },
        ),
    createPricingSet: (payload, onCreated?) =>
        fetchAction(
            CoreAPIService.post(`/core/pricing/pricingSets`, payload),
            types.CREATE_PRICING_SET,
            {},
            {
                success: (dispatch, response) => {
                    if (onCreated) {
                        onCreated(dispatch, response);
                        return;
                    }
                    //Redirect to new created id ?
                    dispatch(push(paths.pricingManagePrices));
                },
            },
        ),
    editPricingSet: (id, payload) =>
        fetchAction(
            CoreAPIService.put(`/core/pricing/pricingSets/${id}`, payload),
            types.EDIT_AREA,
            { id },
            {
                success: (dispatch) => {
                    dispatch(push(`${paths.pricingManagePrices}/${id}`));
                },
            },
        ),
    deletePricingSet: (id, parent_id?) =>
        fetchAction(CoreAPIService.delete(`/core/pricing/pricingSets/${id}`), types.DELETE_PRICING_SET, {
            id,
            parent_id,
        }),

    getCompanyToPricingSets: () =>
        fetchAction(CoreAPIService.get(`/core/pricing/companyToPricingSets`), types.GET_COMPANY_PRICING_SET, {}),
    createCompanyToPricingSets: (company, payload, callback?) =>
        fetchAction(
            CoreAPIService.post(`/core/pricing/companyToPricingSets`, payload),
            types.CREATE_COMPANY_PRICING_SET,
            {
                id: payload.pricing_set_id,
                company: company,
            },
            { success: callback },
        ),
    deleteCompanyToPricingSets: (id, company_to_pricing_set_id, idc?, callback?) =>
        fetchAction(
            CoreAPIService.delete(`/core/pricing/companyToPricingSets/${company_to_pricing_set_id}`),
            types.DELETE_COMPANY_PRICING_SET,
            { id, company_to_pricing_set_id, idc: idc ?? null },
            { success: callback },
        ),

    getGroupToPricingSets: () =>
        fetchAction(CoreAPIService.get(`/core/pricing/groupToPricingSets`), types.GET_GROUP_PRICING_SET, {}),
    // pricing_set carries the picked sheet so the reducer can show the new row
    // without a re-fetch: the API answers with the link, which has no name.
    // idc is the key the companies slice is stored under, passed rather than
    // derived - the slice is keyed by the route param, not by company.id.
    createGroupToPricingSets: (idc, pricing_set, payload, callback?) =>
        fetchAction(
            CoreAPIService.post(`/core/pricing/groupToPricingSets`, payload),
            types.CREATE_GROUP_PRICING_SET,
            {
                id: payload.pricing_set_id,
                idc,
                pricing_set,
            },
            { success: callback },
        ),
    deleteGroupToPricingSets: (id, group_to_pricing_set_id, idc?, callback?) =>
        fetchAction(
            CoreAPIService.delete(`/core/pricing/groupToPricingSets/${group_to_pricing_set_id}`),
            types.DELETE_GROUP_PRICING_SET,
            { id, group_to_pricing_set_id, idc: idc ?? null },
            { success: callback },
        ),

    getCompanyToExtras: () =>
        fetchAction(CoreAPIService.get(`/core/pricing/companyToExtras`), types.GET_COMPANY_EXTRA, {}),
    createCompanyToExtras: (company, payload, callback?) =>
        fetchAction(
            CoreAPIService.post(`/core/pricing/companyToExtras`, payload),
            types.CREATE_COMPANY_EXTRA,
            { id: payload.extra_id, company: company },
            { success: callback },
        ),
    deleteCompanyToExtras: (id, company_to_extra_id) =>
        fetchAction(
            CoreAPIService.delete(`/core/pricing/companyToExtras/${company_to_extra_id}`),
            types.DELETE_COMPANY_EXTRA,
            {
                id,
                company_to_extra_id,
            },
        ),

    getServices: () => fetchAction(CoreAPIService.get(`/core/pricing/services`), types.GET_SERVICES, {}),

    getTariffs: () => fetchAction(CoreAPIService.get(`/core/pricing/tariffs`), types.GET_TARIFFS, {}),
    getTariff: (id) => fetchAction(CoreAPIService.get(`/core/pricing/tariffs/${id}`), types.GET_TARIFF, { id }),
    // callback (optional): called instead of the redirect, to chain another save (ex: return tariff)
    createTariff: (payload, callback?) =>
        fetchAction(
            CoreAPIService.post(`/pricing/tariffs`, payload),
            types.CREATE_TARIFF,
            {},
            {
                success: (dispatch, response) => {
                    if (callback) {
                        callback(dispatch, response);
                        return;
                    }
                    const { pricing_set_id } = response.payload?.data;
                    dispatch(push(`${paths.pricingManagePrices}/${pricing_set_id}`));
                },
            },
        ),
    editTariff: (id, payload, callback?) =>
        fetchAction(
            CoreAPIService.put(`/pricing/tariffs/${id}`, payload),
            types.EDIT_TARIFF,
            { id },
            {
                success: (dispatch, response) => {
                    if (callback) {
                        callback(dispatch, response);
                        return;
                    }
                    const { pricing_set_id } = response.payload?.data;
                    dispatch(push(`${paths.pricingManagePrices}/${pricing_set_id}`));
                },
            },
        ),
    deleteTariff: (idp, idt) =>
        fetchAction(CoreAPIService.delete(`/core/pricing/tariffs/${idt}`), types.DELETE_TARIFF, { idp, idt }),
    searchTariffs: (payload, callback?, onError?) =>
        fetchAction(
            CoreAPIService.post(`/core/pricing/tariffs/search`, payload),
            types.SEARCH_TARIFFS,
            {
                id: payload?.pricing_set_id,
            },
            { success: callback, error: onError },
        ),
    // computeTariff: (payload) => (Actions.fetchAction(CoreAPIService.post(`/core/pricing/tariffs/calculate`, payload), pricingTypes.COMPUTE_TARIFF, { id: payload?.pricing_set_id })),
    computeTariff: (payload) =>
        fetchAction(CoreAPIService.post(`/pricing/price/calculate_and_explain`, payload), types.COMPUTE_TARIFF, {
            id: payload?.pricing_set_id,
        }),
    resetLocalTariff: () => ({ type: types.RESET_LOCAL_TARIFF }),

    getExtras: () => fetchAction(CoreAPIService.get(`/core/pricing/extras`), types.GET_EXTRAS, {}),
    getExtra: (id) => fetchAction(CoreAPIService.get(`/core/pricing/extras/${id}`), types.GET_EXTRA, { id }),
    createExtra: (payload) =>
        fetchAction(
            CoreAPIService.post(`/pricing/extras`, payload),
            types.CREATE_EXTRA,
            {},
            {
                success: (dispatch, response) => {
                    dispatch(push(paths.pricingManageFees));
                },
            },
        ),
    editExtra: (id, payload) =>
        fetchAction(
            CoreAPIService.put(`/pricing/extras/${id}`, payload),
            types.EDIT_EXTRA,
            { id },
            {
                success: (dispatch, response) => {
                    dispatch(push(paths.pricingManageFees));
                },
            },
        ),
    deleteExtras: (id) => fetchAction(CoreAPIService.delete(`/core/pricing/extras/${id}`), types.DELETE_EXTRA, { id }),
    getExtraTypes: () => fetchAction(CoreAPIService.get(`/core/pricing/extraTypes`), types.GET_EXTRA_TYPES, {}),
};

const localState = (state: any): PricingState => state[name] || initialState;
export const selectors = {
    pricing: (state) => localState(state),
    areas: (state) => localState(state).areas,
    root_areas: (state) => localState(state).root_areas?.data,
    root_areas_tree: (state) => localState(state).root_tree_areas?.data,
    extras: (state) => localState(state).extras_list,
    extra_types: (state) => localState(state).extras_types,
    sets: (state) => localState(state).sets,
    prices: (state) => localState(state).pricing_sets?.data,
    pricesTree: (state) => localState(state).pricing_sets_tree?.data,
    services: (state) => localState(state).services?.data,
    tariffs: (state) => localState(state).tariffs,
    tariffs_list: (state) => localState(state).tariffs_list,
    price: (state) => localState(state).price,
};
